import { sentenceCase } from "@redotech/string/transform";
import type { UnionToIntersection } from "@redotech/util/type";
import type { ComponentType } from "react";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { useSearchParams } from "react-router-dom";

export interface TabDefinition<P = any> {
  component: React.ComponentType<P>;
  label?: string;
  badgeText?: string;
}

export interface TabItem<T> {
  key: T;
  label: string;
  badgeText?: string;
}

// Helper type to extract props from a component. A component with no props
// parameter infers P as unknown, which would absorb the whole union below,
// so it contributes an empty requirement instead.
type ComponentProps<C> =
  C extends ComponentType<infer P> ? (unknown extends P ? {} : P) : never;

// Every tab component's props combined, so one set of attributes satisfies
// whichever component is selected at runtime.
type TabConfigProps<TDefs extends Record<string, TabDefinition>> =
  UnionToIntersection<
    { [K in keyof TDefs]: ComponentProps<TDefs[K]["component"]> }[keyof TDefs]
  >;

export interface UseTabNavigationReturn<
  T,
  TDefs extends Record<string, TabDefinition>,
> {
  selectedTab: T;
  tabs: TabItem<T>[];
  SelectedTabComponent: React.ComponentType<TabConfigProps<TDefs>>;
  setSelectedTab: (key: T) => void;
}

/**
 * Helper function to create type-safe tab config.
 */
export function createTabConfig<
  TEnum extends Record<string, string>,
  TDefs extends Record<TEnum[keyof TEnum], TabDefinition>,
>(_enumObject: TEnum, definitions: TDefs): TDefs {
  return definitions;
}

/**
 * Hook for managing tab navigation with URL synchronization.
 */
export function useTabs<TDefs extends Record<string, TabDefinition>>({
  tabConfig,
  defaultTab,
  filterTabs,
  urlParam = "tab",
  onTabChange,
}: {
  tabConfig: TDefs;
  defaultTab?: keyof TDefs;
  filterTabs?: (tabKey: keyof TDefs, tabDef: TabDefinition) => boolean;
  urlParam?: string;
  onTabChange?: (newTab: keyof TDefs, previousTab: keyof TDefs) => void;
}): UseTabNavigationReturn<keyof TDefs, TDefs> {
  const [searchParams, setSearchParams] = useSearchParams();

  const filteredEntries = useMemo(() => {
    const entries = Object.entries(tabConfig) as [keyof TDefs, TabDefinition][];
    return entries.filter(([key, def]) => !filterTabs || filterTabs(key, def));
  }, [tabConfig, filterTabs]);

  // Use provided defaultTab if it survives the filter, else first surviving tab
  const computedDefaultTab =
    defaultTab && filteredEntries.some(([key]) => key === defaultTab)
      ? defaultTab
      : // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        ((filteredEntries[0]?.[0] ?? Object.keys(tabConfig)[0]) as keyof TDefs);
  const previousTabRef = useRef<keyof TDefs>(computedDefaultTab);

  // Get current tab from URL or use default; a filtered-out tab is invalid so
  // its content never renders orphaned from the tab strip
  const urlTab = searchParams.get(urlParam);
  const isValidTab =
    !!urlTab && filteredEntries.some(([key]) => key === urlTab);
  const selectedTab = (isValidTab ? urlTab : computedDefaultTab) as keyof TDefs;

  const tabs = useMemo(
    () =>
      filteredEntries.map(([key, def]) => ({
        key,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
        label: def.label ?? sentenceCase(key as string),
        badgeText: def.badgeText,
      })),
    [filteredEntries],
  );

  // Redirect to default tab if URL has an invalid or filtered-out tab
  useEffect(() => {
    if (urlTab && !isValidTab) {
      const params = new URLSearchParams(searchParams);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      params.set(urlParam, computedDefaultTab as string);
      setSearchParams(params, { replace: true });
    }
  }, [
    urlTab,
    isValidTab,
    computedDefaultTab,
    urlParam,
    searchParams,
    setSearchParams,
  ]);

  // Trigger callback when tab changes
  useEffect(() => {
    const previous = previousTabRef.current;
    if (previous !== selectedTab) {
      onTabChange?.(selectedTab, previous);
      previousTabRef.current = selectedTab;
    }
  }, [selectedTab, onTabChange]);

  // Update URL when tab is selected
  const setSelectedTab = useCallback(
    (key: keyof TDefs) => {
      const newParams = new URLSearchParams(searchParams);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      newParams.set(urlParam, key as string);
      setSearchParams(newParams);
    },
    [searchParams, urlParam, setSearchParams],
  );

  return {
    selectedTab,
    tabs,
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    SelectedTabComponent: tabConfig[selectedTab].component as ComponentType<
      TabConfigProps<TDefs>
    >,
    setSelectedTab,
  };
}
